// src/layouts/AuthLayout.jsx
import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

/** ============ Helpers ============ */
// Fix 100vh trên iOS/Android (thanh URL thay đổi)
const setVhUnit = () => {
  const vh = (window.visualViewport?.height || window.innerHeight) * 0.01;
  document.documentElement.style.setProperty("--vh", `${vh}px`);
};
const useVhFix = () => {
  useEffect(() => {
    setVhUnit();
    const handler = () => setVhUnit();
    window.addEventListener("resize", handler);
    window.addEventListener("orientationchange", handler);
    window.visualViewport?.addEventListener?.("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("orientationchange", handler);
      window.visualViewport?.removeEventListener?.("resize", handler);
    };
  }, []);
};

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)"; // giống easeOut tiêu chuẩn

const AuthLayout = ({ isMobile }) => {
  useVhFix();
  const location = useLocation();

  const showFooter = location.pathname === "/login";
  // Reduced motion
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(m.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, []);

  // Mobile BG load tracking
  const [mbTopLoaded, setMbTopLoaded] = useState(false);
  const [mbBottomLoaded, setMbBottomLoaded] = useState(false);
  const mobileAllLoaded = mbTopLoaded && mbBottomLoaded;

  // Desktop BG load tracking
  const [bgLoaded, setBgLoaded] = useState(false);

  // Trigger animation flags (thay cho motion initial/animate)
  const [bgVisible, setBgVisible] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);

  // Khi ảnh đã load, mới cho phép hiện/animate để tránh giật
  useEffect(() => {
    if (isMobile) {
      if (mobileAllLoaded) {
        if (reducedMotion) {
          setBgVisible(true);
          setCardVisible(true);
          setContentVisible(true);
        } else {
          // Sequencing nhẹ nhàng
          const t1 = setTimeout(() => setBgVisible(true), 20);
          const t2 = setTimeout(() => setCardVisible(true), 80);
          const t3 = setTimeout(() => setContentVisible(true), 140);
          return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
          };
        }
      }
    } else {
      if (bgLoaded) {
        if (reducedMotion) {
          setBgVisible(true);
          setCardVisible(true);
          setContentVisible(true);
        } else {
          const t1 = setTimeout(() => setBgVisible(true), 10);
          const t2 = setTimeout(() => setCardVisible(true), 80);
          const t3 = setTimeout(() => setContentVisible(true), 160);
          return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
          };
        }
      }
    }
  }, [isMobile, mobileAllLoaded, bgLoaded, reducedMotion]);

  /** ============ MOBILE ============ */
  if (isMobile) {
    return (
      <div
        className="relative w-full"
        style={{
          height: "calc(var(--vh, 1vh) * 100)",
          minHeight: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Mobile Background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: bgVisible ? 1 : 0,
            transform: bgVisible ? "scale(1)" : "scale(1.01)",
            transition: reducedMotion
              ? "none"
              : `opacity 450ms ${EASE}, transform 450ms ${EASE}`,
          }}
        >
          <div className="flex h-full flex-col justify-between">
            <img
              src="/images/mobile-top-bg.png"
              alt=""
              loading="eager"
              onLoad={() => setMbTopLoaded(true)}
              className="w-full object-cover transition-opacity duration-300"
              style={{ opacity: mbTopLoaded ? 1 : 0 }}
            />
            <img
              src="/images/mobile-bottom-bg.png"
              alt=""
              loading="eager"
              onLoad={() => setMbBottomLoaded(true)}
              className="w-full object-cover transition-opacity duration-300"
              style={{ opacity: mbBottomLoaded ? 1 : 0 }}
            />
          </div>
        </div>

        {/* Content */}
        <div
          className="absolute inset-0 flex items-center justify-end px-6 overflow-auto"
          style={{
            opacity: cardVisible ? 1 : 0,
            transform: cardVisible ? "translateY(0)" : "translateY(20px)",
            transition: reducedMotion
              ? "none"
              : `opacity 400ms ${EASE} 40ms, transform 400ms ${EASE} 40ms`,
          }}
        >
          <div
            className="w-full"
            style={{
              filter: contentVisible ? "blur(0px)" : "blur(4px)",
              opacity: contentVisible ? 1 : 0.9,
              transition: reducedMotion
                ? "none"
                : `filter 350ms ${EASE}, opacity 350ms ${EASE}`,
            }}
          >
            <Outlet />
          </div>
        </div>

        {showFooter && (
          <footer className="pointer-events-auto absolute bottom-0 left-0 w-full px-4 pb-3 text-center">
            <p className="mx-auto max-w-screen-md text-[8px] font-be-vietnam-semiBold">
              Thông tin chi tiết vui lòng liên hệ số:&nbsp;
              <a
                href="tel:0819009697"
                className="underline hover:underline-offset-2 hover:opacity-90 text-blue-500 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
              >
                08.1900.9697
              </a>
              &nbsp;•&nbsp; Website:&nbsp;
              <a
                href="https://sohuutritue.vn/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:underline-offset-2 hover:opacity-90 text-blue-500 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
              >
                https://sohuutritue.vn/
              </a>
            </p>
          </footer>
        )}

        {/* Local CSS only for this layout */}
        <style>{`
          @media (prefers-reduced-motion: reduce) {
            * { animation: none !important; transition: none !important; }
          }
        `}</style>
      </div>
    );
  }

  /** ============ DESKTOP ============ */
  return (
    <div
      className="relative w-full"
      style={{ height: "100vh", overflow: "hidden" }}
    >
      {/* Background */}
      <img
        src="/images/Login-bg.jpg"
        alt="Background"
        loading="eager"
        onLoad={() => setBgLoaded(true)}
        className="absolute left-0 top-0 h-full w-full object-cover will-change-transform"
        style={{
          opacity: bgVisible ? 1 : 0,
          transform: bgVisible ? "scale(1)" : "scale(1.01)",
          transition: reducedMotion
            ? "none"
            : `opacity 500ms ${EASE}, transform 500ms ${EASE}`,
        }}
      />

      {/* Dim overlay nhẹ (không dùng motion) */}
      <div
        className="pointer-events-none absolute inset-0 bg-black"
        style={{
          opacity: bgVisible ? 0.08 : 0,
          transition: reducedMotion ? "none" : `opacity 400ms ${EASE} 40ms`,
        }}
      />
      {/* Content (panel) */}
      <div className="absolute inset-0 flex items-center justify-end mr-[10vw] z-100">
        <div
          className="relative flex max-w-lg lap:max-w-xl w-2/5 p-6 rounded-xl bg-white/90 backdrop-blur-sm shadow-lg overflow-auto min-h-[60vh] max-h-[95vh] will-change-transform"
          style={{
            opacity: cardVisible ? 1 : 0,
            transform: cardVisible ? "translateX(0)" : "translateX(24px)",
            transition: reducedMotion
              ? "none"
              : `opacity 450ms ${EASE} 40ms, transform 450ms ${EASE} 40ms`,
          }}
        >
          <div
            className="w-full"
            style={{
              opacity: contentVisible ? 1 : 0,
              transition: reducedMotion
                ? "none"
                : `opacity 300ms ${EASE} 100ms`,
            }}
          >
            <Outlet />
          </div>
        </div>
      </div>
      {showFooter && (
        <footer className="pointer-events-auto absolute bottom-0 left-0 w-full px-4 pb-3 text-center">
          <p className="mx-auto max-w-screen-md text-xs sm:text-sm text-white/95 font-be-vietnam-semiBold">
            Thông tin chi tiết vui lòng liên hệ số:&nbsp;
            <a
              href="tel:0819009697"
              className="underline hover:underline-offset-2 hover:opacity-90 text-blue-500 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
            >
              08.1900.9697
            </a>
            &nbsp;•&nbsp; Website:&nbsp;
            <a
              href="https://sohuutritue.vn/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:underline-offset-2 hover:opacity-90 text-blue-500 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
            >
              https://sohuutritue.vn/
            </a>
          </p>
        </footer>
      )}
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
};

export default AuthLayout;
